import { createContext } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import { cloneElement } from 'preact';

export const LanguageContext = createContext();

export const useTranslation = () => useContext(LanguageContext);

export const SUPPORTED_LANGUAGES = [
  'en', 'ar', 'fr', 'hi', 'id', 'ja', 'ko', 'ms', 'pt', 'es', 'th', 'vi'
];

export function getLanguage() {
  const urlParams = new URLSearchParams(window.location.search);
  const hl = urlParams.get('hl');
  if (hl && SUPPORTED_LANGUAGES.includes(hl)) {
    return hl;
  }

  if (navigator.languages && navigator.languages.length) {
    for (const lang of navigator.languages) {
      if (SUPPORTED_LANGUAGES.includes(lang)) {
        return lang;
      }
      const baseLang = lang.split('-')[0];
      if (SUPPORTED_LANGUAGES.includes(baseLang)) {
        return baseLang;
      }
    }
  }

  return 'en';
}

export const I18nProvider = ({ translations, children }) => {
  const [t, setT] = useState(() => (key) => key);
  useEffect(() => {
    // If the translations are not yet loaded, do nothing.
    if (!translations) {
      return;
    }

    const lang = getLanguage();
    const loadedTranslations = translations[lang] || translations['en'];

    setT(() => (key, replacements = {}) => {
      if (!loadedTranslations) {
        return key;
      }
      const translation = loadedTranslations[key] || key;
      const hasComponentReplacement = Object.values(replacements).some(r => typeof r === 'object' && r !== null);

      const parts = translation.split(/(<\d+>.*?<\/\d+>|<\d+\/>|<\d+>)/g).filter(Boolean);

      const result = parts.map(part => {
        let match;

        match = part.match(/<(\d+)>(.*?)<\/(\d+)>/);
        if (match) {
          const placeholderIndex = match[1];
          const innerText = match[2];
          const replacement = replacements[placeholderIndex];

          if (replacement && typeof replacement === 'object') {
            return cloneElement(replacement, {}, innerText);
          }
          if (replacement && typeof replacement === 'string') {
            const tagNameMatch = replacement.match(/<([a-zA-Z0-9]+)/);
            if (tagNameMatch) {
              const tagName = tagNameMatch[1];
              return `${replacement}${innerText}</${tagName}>`;
            }
          }
          return innerText;
        }

        match = part.match(/<(\d+)\/>/);
        if (match) {
          const placeholderIndex = match[1];
          return replacements[placeholderIndex] || '';
        }

        match = part.match(/<(\d+)>/);
        if (match) {
          const placeholderIndex = match[1];
          return replacements[placeholderIndex] || '';
        }

        return part;
      });

      if (hasComponentReplacement) {
        return result;
      }
      return result.join('');
    });
  }, [translations]);

  return (
    <LanguageContext.Provider value={{ t }}>
      {children}
    </LanguageContext.Provider>
  );
};
